import { ReactNode } from "react";
import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { formatDate } from "@/shared/lib/utilities/date";
import {
  ListRequest,
  useHtzSummaryListQuery,
} from "@/services/backend/htz/summary/api/summary-api";
import { Summary } from "@/services/backend/htz/summary/types/summary";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";

export function SummariesTable({
  request,
  onRequestChange,
  renderRowLink,
  hidePagination = false,
  hideBorder = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (summary: Summary, children: ReactNode) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  const { data: list, isLoading, error } = useHtzSummaryListQuery(request);

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("Datum")}</TableHead>
              <TableHead className="w-full">{t("Titel")}</TableHead>
              <TableHead>{t("Anlagen")}</TableHead>
              <TableHead className="whitespace-nowrap">
                {t("Aktualisiert am")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={6} />}
            {error && <TableRtkErrorRow error={error} colSpan={6} />}
            {emptyList && <TableEmptyRow colSpan={6} />}
            {list &&
              list.items.map((summary) => (
                <SummaryTableRow
                  key={summary.id}
                  summary={summary}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

function SummaryTableRow({
  summary,
  renderRowLink,
}: {
  summary: Summary;
  renderRowLink: (summary: Summary, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        {renderRowLink(
          summary,
          <ReferenceNumber referenceNumber={summary.referenceNumber} />,
        )}
      </TableCell>
      <TableCell>
        <DocumentStateBadge document={summary} />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {summary.date ? (
          formatDate(summary.date)
        ) : (
          <span className="text-muted-foreground">{t("-")}</span>
        )}
      </TableCell>
      <TableCell>{summary.title}</TableCell>
      <TableCell>{summary.items.length}</TableCell>
      <TableCell className="whitespace-nowrap">
        {formatDate(summary.updatedAt)}
      </TableCell>
    </TableRow>
  );
}

export function SummaryTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <OrderBySelect<string>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Datum"), value: "date" },
          { label: t("Titel"), value: "title" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Aktualisiert am"), value: "updatedAt" },
          { label: t("Festgeschrieben am"), value: "stateSetAt" },
        ]}
      />
    </div>
  );
}

export function useDefaultHtzSummaryListRequest(processId: UUID): ListRequest {
  return {
    process: { active: true, values: [processId] },
    customer: { active: false, values: [] },
    state: { active: false, values: [] },
    orderBy: [{ field: "date" }],
    limit: 50,
    offset: 0,
  };
}

export function useDefaultCustomerHtzSummaryListRequest(
  companyId: UUID,
  customerId: UUID,
): ListRequest {
  return {
    company: { active: true, values: [companyId] },
    customer: { active: true, values: [customerId] },
    state: { active: false, values: [] },
    orderBy: [{ field: "date" }],
    limit: 50,
    offset: 0,
  };
}
